import React from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    Button,
    Box,
    Switch,
    FormControlLabel,
    styled
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const StyledDialogTitle = styled('div')({
    fontSize: '1.8rem',
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#025366',
    padding: '20px'
});

const StyledDialogContent = styled(DialogContent)({
    textAlign: 'center',
    padding: '20px 30px',
});

const PaymentMethodSelectionDialog = ({
    open,
    onClose,
    documents,
    isBackgroundImport,
    useAdvancedMapping,
    setUseAdvancedMapping,
    filePaymentMethodMap,
    setFilePaymentMethodMap,
    onPaymentMethodSelect,
    onConfigureMapping
}) => {
    const { t } = useTranslation();

    return (
        <Dialog open={open} onClose={onClose}>
            <StyledDialogTitle>{t('SelectPaymentMethod')}</StyledDialogTitle>
            <StyledDialogContent>
                <Typography variant="body2" color="#025366" sx={{ mb: 2 }}>
                    {t('WhichPaymentMethodDoesThisDocumentRepresent')}
                </Typography>

                {/* Toggle for advanced mapping - Available for all multi-file uploads */}
                {documents.length > 1 && (
                    <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={useAdvancedMapping}
                                    onChange={(e) => {
                                        const enabled = e.target.checked;
                                        setUseAdvancedMapping(enabled);
                                        if (enabled) {
                                            // Initialize mapping array with default values
                                            setFilePaymentMethodMap(documents.map(() => 'checks'));
                                        } else {
                                            setFilePaymentMethodMap([]);
                                        }
                                    }}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#304FFF'
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#304FFF'
                                        }
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {t('AssignDifferentTypesToFiles')} ({documents.length} {t('files')})
                                </Typography>
                            }
                        />
                    </Box>
                )}

                {useAdvancedMapping ? (
                    <Button
                        variant="contained"
                        onClick={onConfigureMapping}
                        sx={{
                            width: '100%',
                            backgroundColor: '#304FFF',
                            '&:hover': { backgroundColor: '#1E40FF' }
                        }}
                    >
                        {t('ConfigureFileMapping')}
                    </Button>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => onPaymentMethodSelect('checks')}
                            sx={{
                                borderColor: '#304FFF',
                                color: '#304FFF',
                                '&:hover': { backgroundColor: '#304FFF', color: 'white' }
                            }}
                        >
                            {t('Checks')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => onPaymentMethodSelect('cash')}
                            sx={{
                                borderColor: '#304FFF',
                                color: '#304FFF',
                                '&:hover': { backgroundColor: '#304FFF', color: 'white' }
                            }}
                        >
                            {t('Cash')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => onPaymentMethodSelect('creditcards')}
                            sx={{
                                borderColor: '#304FFF',
                                color: '#304FFF',
                                '&:hover': { backgroundColor: '#304FFF', color: 'white' }
                            }}
                        >
                            {t('CreditCards')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => onPaymentMethodSelect('banktransactions')}
                            sx={{
                                borderColor: '#304FFF',
                                color: '#304FFF',
                                '&:hover': { backgroundColor: '#304FFF', color: 'white' }
                            }}
                        >
                            {t('BankTransactions')}
                        </Button>
                        {/* Hide Receipt option for receipt/receipttax uploads */}
                        {isBackgroundImport && (
                            <Button
                                variant="outlined"
                                onClick={() => onPaymentMethodSelect('receipt')}
                                sx={{
                                    borderColor: '#F9AA2A',
                                    color: '#F9AA2A',
                                    '&:hover': { backgroundColor: '#F9AA2A', color: 'white' }
                                }}
                            >
                                {t('Receipt')}
                            </Button>
                        )}
                    </Box>
                )}
            </StyledDialogContent>
        </Dialog>
    );
};

export default PaymentMethodSelectionDialog;

